import React, { useState, useEffect } from 'react'
import 'bootstrap/dist/css/bootstrap.min.css';
import { Col, Row, Container, Accordion, Table, Form, InputGroup, Modal, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from "react-redux";
import './PriceList.css';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { Search } from 'react-bootstrap-icons';

import { fetchKitchenThingsList } from '../redux/kitchenThingsReducer';
import { fetchLooseThingsList } from '../redux/looseFurnitureReducer';
import { fetchOtherWorksList } from '../redux/otherWorkReducer';
import { fetchBedroomList } from '../redux/bedroomReducer';
import { fetchFoyerThingsList } from '../redux/foyerReducer';
import { fetchLivingThingsList } from '../redux/livingRoomReducer';
import { fetchAccessoriesList } from '../redux/accessoriesReducer';

// ── Dropdown options for the "which price list" popup ───────────────────
const DATABASE_OPTIONS = [
  { value: 'default', label: '1. Default' },
  { value: 'pricelist04072026', label: '2. Price List 04-07-2026' },
];

function PriceList() {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [showMarkup, setShowMarkup] = useState(true); // default: with increment

  // ── projectDatabase detection ───────────────────────────────────────────
  // If localStorage already has a projectDatabase, use it straight away.
  // Otherwise show a popup asking the user which price list to load.
  const [projectDatabase, setProjectDatabase] = useState(
    () => localStorage.getItem('projectDatabase') || ''
  );
  const [showDbModal, setShowDbModal] = useState(!projectDatabase);
  const [selectedDb, setSelectedDb] = useState(DATABASE_OPTIONS[0].value);
  // ────────────────────────────────────────────────────────────────────────

  // ── Detect location from localStorage ──────────────────────────────────
  const userDetailsLocal = localStorage.getItem('userDetails');
  const userDetailsLo = JSON.parse(userDetailsLocal);
  const userLocation = userDetailsLo?.location?.toLowerCase?.() || '';
  const isKKT = userLocation === 'kkt';
  const MARKUP = isKKT ? 1.70 : 1.65; // KKT = 70%, BLR (and others) = 65%
  // ────────────────────────────────────────────────────────────────────────

  // ── Helper to apply markup ──────────────────────────────────────────────
  const applyMarkup = (value) => {
    if (!value || isNaN(value)) return 0;
    return Math.round(parseInt(value) * MARKUP);
  };
  // ────────────────────────────────────────────────────────────────────────

  // ── Helper: get price respecting toggle and item type ──────────────────
  const getPrice = (value, isWoodwork) => {
    if (isWoodwork && showMarkup) return applyMarkup(value);
    return value || 0;
  };
  // ────────────────────────────────────────────────────────────────────────

  // ✅ Single helper — dispatches ALL location + database-dependent room data at once
  // NOTE: `projectLocation` here must be the project's location string (e.g. "KKT" / "BLR"),
  // NOT the react-router location object. Passing the router location object was the bug
  // that caused these fetches to silently fail.
  const dispatchAllRoomData = () => {
    // const args = { location: projectLocation, databaseId: projectDatabase };
    dispatch(fetchKitchenThingsList());
    dispatch(fetchLooseThingsList());
    dispatch(fetchOtherWorksList());
    dispatch(fetchBedroomList());
    dispatch(fetchFoyerThingsList());
    dispatch(fetchLivingThingsList());
    dispatch(fetchAccessoriesList());
  };

  // Fetch room data as soon as we have a valid projectDatabase
  // (either already in localStorage, or just chosen in the popup).
  useEffect(() => {
    if (projectDatabase) {
      dispatchAllRoomData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDatabase]);

  const handleDbConfirm = () => {
    localStorage.setItem('projectDatabase', selectedDb);
    setProjectDatabase(selectedDb);
    setShowDbModal(false);
  };
  // ────────────────────────────────────────────────────────────────────────

  const { kitchenThingsData } = useSelector((state) => state.kitchenThings);
  const { bedroomThingsData } = useSelector((state) => state.bedroomThings);
  const { foyerThingsData } = useSelector((state) => state.foyerThings);
  const { livingThingsData } = useSelector((state) => state.livingThings);
  const { otherWorkThingsData } = useSelector((state) => state.OtherWorkThings);
  const { looseThingsData } = useSelector((state) => state.looseThings);
  const { accessoriesThingsData } = useSelector((state) => state.accessories);

  // ── Filter out removed items + apply search ────────────────────────────
  const filterItemsBySearch = (items) => {
    if (!Array.isArray(items)) return [];
    const visible = items.filter(item => !item.removed);

    if (!searchTerm) return visible;

    return visible.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.material?.some(mat =>
        mat.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mat.subMaterials?.some(sub =>
          sub.subLabel?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
  };
  // ────────────────────────────────────────────────────────────────────────

  const allCategories = [
    { name: 'Kitchen', data: kitchenThingsData },
    { name: 'Bedroom', data: bedroomThingsData },
    { name: 'Foyer', data: foyerThingsData },
    { name: 'Living Room', data: livingThingsData },
    { name: 'Other Works', data: otherWorkThingsData },
    { name: 'Loose Furniture', data: looseThingsData },
    { name: 'Accessories', data: accessoriesThingsData },
  ];

  const matchedCategories = searchTerm
    ? allCategories.filter(cat => filterItemsBySearch(cat.data).length > 0)
    : [];

  const renderItemRow = (item) => {
    const isWoodwork = item.type === 'woodwork';
    return (
      <div key={item.name} style={{
        display: 'flex',
        height: 'auto',
        border: '1px solid whitesmoke',
        borderBottom: '1px solid black',
        justifyContent: 'start',
        alignItems: 'stretch',
      }}>
        {/* Item Name */}
        <div style={{
          width: '250px',
          minWidth: '250px',
          display: 'flex',
          justifyContent: 'start',
          padding: '50px 10px 10px 30px',
          borderRight: '1px solid whitesmoke',
          fontWeight: 'bold',
        }}>
          {item.name}
        </div>

        {/* Material Cards */}
        <div style={{
          fontFamily: "'DM Sans', sans-serif",
          display: 'flex',
          justifyContent: 'start',
          padding: 10,
          alignItems: 'flex-start',
          background: '#f0f3fa',
          flexWrap: 'wrap',
          gap: 10,
          flex: 1,
        }}>
          {item.material?.map((item2, index2) => (
            <div key={item.name + '-' + index2} style={{
              width: 'auto',
              background: '#fff',
              border: '1.5px solid #e4e8f0',
              borderRadius: 10,
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(30,42,74,0.08)',
            }}>
              {/* Card Header */}
              <div style={{
                padding: '11px 16px',
                background: '#f0f4ff',
                borderBottom: '1px solid #e4e8f0',
                fontWeight: 700,
                fontSize: 14,
                color: '#1e2a4a',
              }}>
                {item2.label}
              </div>

              {/* Standard row */}
              <div style={{ padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, color: '#6b7a99', width: 150, whiteSpace: 'normal', wordBreak: 'break-word', padding: 5 }}>
                    standard
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {/* OLD price */}
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      background: '#fff7ed', border: '1px solid #fed7aa',
                      borderRadius: 6, padding: '5px 8px', gap: 3,
                    }}>
                      <span style={{ fontSize: 9, color: '#9a3412', fontWeight: 700 }}>OLD</span>
                      <span style={{ color: '#9a3412', fontSize: 12 }}>₹</span>
                      <input
                        type="number"
                        value={getPrice(item2.oldValue, isWoodwork)}
                        readOnly
                        style={{
                          border: 'none', background: 'transparent',
                          width: 52, fontSize: 13, fontWeight: 600,
                          color: '#9a3412', outline: 'none',
                          fontFamily: "'DM Sans', sans-serif", textAlign: 'right',
                        }}
                      />
                    </div>

                    <span style={{ fontSize: 10, color: '#94a3b8' }}>→</span>

                    {/* NEW price */}
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      background: '#eff6ff', border: '1px solid #bfdbfe',
                      borderRadius: 6, padding: '5px 8px', gap: 3,
                    }}>
                      <span style={{ fontSize: 9, color: '#1d4ed8', fontWeight: 700 }}>NEW</span>
                      <span style={{ color: '#1d4ed8', fontSize: 12 }}>₹</span>
                      <input
                        type="number"
                        value={getPrice(item2.value, isWoodwork)}
                        readOnly
                        style={{
                          border: 'none', background: 'transparent',
                          width: 52, fontSize: 13, fontWeight: 600,
                          color: '#1d4ed8', outline: 'none',
                          fontFamily: "'DM Sans', sans-serif", textAlign: 'right',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SubMaterials rows */}
              {item2.subMaterials && item2.subMaterials.length > 0 && (
                <div style={{ padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {item2.subMaterials.map((item3, index3) => (
                    <div key={item.name + '-' + item2.label + '-' + index3}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12.5, color: '#6b7a99', width: 150, whiteSpace: 'normal', wordBreak: 'break-word', padding: 5 }}>
                        {item3.subLabel}
                      </span>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {/* SubMaterial OLD price */}
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          background: '#fff7ed', border: '1px solid #fed7aa',
                          borderRadius: 6, padding: '5px 8px', gap: 3,
                        }}>
                          <span style={{ fontSize: 9, color: '#9a3412', fontWeight: 700 }}>OLD</span>
                          <span style={{ color: '#9a3412', fontSize: 12 }}>₹</span>
                          <input
                            type="number"
                            value={getPrice(item3.subOldValue, isWoodwork)}
                            readOnly
                            style={{
                              border: 'none', background: 'transparent',
                              width: 52, fontSize: 13, fontWeight: 600,
                              color: '#9a3412', outline: 'none',
                              fontFamily: "'DM Sans', sans-serif", textAlign: 'right',
                            }}
                          />
                        </div>

                        <span style={{ fontSize: 10, color: '#94a3b8' }}>→</span>

                        {/* SubMaterial NEW price */}
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          background: '#eff6ff', border: '1px solid #bfdbfe',
                          borderRadius: 6, padding: '5px 8px', gap: 3,
                        }}>
                          <span style={{ fontSize: 9, color: '#1d4ed8', fontWeight: 700 }}>NEW</span>
                          <span style={{ color: '#1d4ed8', fontSize: 12 }}>₹</span>
                          <input
                            type="number"
                            value={getPrice(item3.subValue, isWoodwork)}
                            readOnly
                            style={{
                              border: 'none', background: 'transparent',
                              width: 52, fontSize: 13, fontWeight: 600,
                              color: '#1d4ed8', outline: 'none',
                              fontFamily: "'DM Sans', sans-serif", textAlign: 'right',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Container fluid>
      {/* ── Popup: which price list to load, shown only when projectDatabase is missing ── */}
      <Modal show={showDbModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Select Price List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>No project database was found for this session. Please choose which price list you'd like to view.</p>
          <Form.Select value={selectedDb} onChange={(e) => setSelectedDb(e.target.value)}>
            {DATABASE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleDbConfirm}>Load Price List</Button>
        </Modal.Footer>
      </Modal>

      {/* Rest of the page only renders once we have a projectDatabase */}
      {projectDatabase && (
        <>
          <Row>
            <Col className='px-5 pb-5'>
              <img src={require('../assests/sitelogo.png')} alt='Dashboard' className='logoImg' />
            </Col>
          </Row>

          {/* Location badge + Markup Toggle */}
          <Row className="px-3 mb-2 align-items-center" style={{ gap: 12 }}>
            <Col xs="auto">
              <span style={{
                display: 'inline-block',
                background: isKKT ? '#fef3c7' : '#eff6ff',
                color: isKKT ? '#92400e' : '#1d4ed8',
                border: `1px solid ${isKKT ? '#fcd34d' : '#bfdbfe'}`,
                borderRadius: 20,
                padding: '4px 14px',
                fontSize: 13,
                fontWeight: 700,
              }}>
                {isKKT ? 'KKT Price List' : 'BLR Price List'}
              </span>
            </Col>

            <Col xs="auto">
              {/* Toggle pill button */}
              <div
                onClick={() => setShowMarkup(prev => !prev)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: showMarkup ? '#dcfce7' : '#f1f5f9',
                  border: `1.5px solid ${showMarkup ? '#86efac' : '#cbd5e1'}`,
                  borderRadius: 20,
                  padding: '4px 14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Track */}
                <div style={{
                  width: 36,
                  height: 20,
                  borderRadius: 10,
                  background: showMarkup ? '#22c55e' : '#94a3b8',
                  position: 'relative',
                  transition: 'background 0.2s ease',
                  flexShrink: 0,
                }}>
                  {/* Knob */}
                  <div style={{
                    position: 'absolute',
                    top: 3,
                    left: showMarkup ? 18 : 3,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left 0.2s ease',
                  }} />
                </div>
                <span style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: showMarkup ? '#15803d' : '#475569',
                }}>
                  {showMarkup ? 'Active' : 'Base'}
                </span>
              </div>
            </Col>
          </Row>

          {/* Search Bar */}
          <Row className="mb-4 px-3">
            <Col md={6} className="mx-auto">
              <InputGroup>
                <InputGroup.Text><Search /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search items by name or material..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <InputGroup.Text style={{ cursor: 'pointer' }} onClick={() => setSearchTerm('')}>
                    ✕
                  </InputGroup.Text>
                )}
              </InputGroup>
            </Col>
          </Row>

          {/* Search Results */}
          {searchTerm && (
            <Row className="mb-4">
              <Col>
                <Accordion defaultActiveKey="search-results">
                  <Accordion.Item eventKey="search-results">
                    <Accordion.Header>Search Results</Accordion.Header>
                    <Accordion.Body>
                      {matchedCategories.length === 0 ? (
                        <p className="text-center">No items found matching "{searchTerm}"</p>
                      ) : (
                        matchedCategories.map(category => (
                          <div key={category.name}>
                            <h5 className="mt-3 mb-3">{category.name}</h5>
                            {filterItemsBySearch(category.data).map(item => renderItemRow(item))}
                          </div>
                        ))
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
          )}

          {/* Accordion Categories */}
          <Row>
            <Accordion>
              {allCategories.map((cat, idx) => (
                <Accordion.Item eventKey={String(idx)} key={cat.name}>
                  <Accordion.Header>{cat.name}</Accordion.Header>
                  <Accordion.Body>
                    {filterItemsBySearch(cat.data).map(item => renderItemRow(item))}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            <ReactHTMLTableToExcel
              id="test-table-xls-button"
              className="download-table-xls-button mt-3"
              table={'sanjay'}
              filename="price-list"
              sheet="price-list"
              buttonText="Download as Excel"
            />

            {/* Hidden Excel Table */}
            <Table id={'sanjay'} striped bordered hover className="border d-none">
              <tbody>
                {allCategories.map(cat => (
                  <React.Fragment key={cat.name}>
                    <tr><th>{cat.name}</th></tr>
                    {filterItemsBySearch(cat.data).map((item, index) => (
                      <tr key={`${cat.name}-${index}`}>
                        <td><p style={{ fontWeight: 'bold' }}>{item.name}</p></td>
                        {item.material?.map((item2, index2) => (
                          <td key={`${cat.name}-mat-${index2}`}>
                            <p>{item2.label}</p>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </Row>
        </>
      )}
    </Container>
  );
}

export default PriceList;